import { useCreateThread, useThreads } from '@liveblocks/react'
import { PlusIcon } from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'

import { type EventProperties, useTrackCallback } from '@/lib/analytics/events'
import { sortByTimestamp } from '@/lib/date/date'
import { scrollIntoViewWhenStops } from '@/lib/ui/animation'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import { useAssessmentAnalytics } from '@/pages/assessment/analytics'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Empty } from '@/components/lemmala-design-system/empty/empty'
import { Loading } from '@/components/lemmala-design-system/loading/loading'
import { Modal } from '@/components/lemmala-design-system/modal/modal'
import { CommentThread } from '@/components/shared/comment/comment-thread'
import { CommentsLoadError } from '@/components/shared/comment/comments-load-error'
import { COMMENT_EVENT_SOURCES, commentAnchorId } from '@/components/shared/comment/consts'
import { useSendCommentNotification } from '@/components/shared/comment/use-send-comment-notification'
import { isInternalCommentThread } from '@/components/shared/comment/utils'
import { mentionEventProps } from '@/components/shared/comment/utils'

import { ControlCommentComposer } from './control-comment-composer'
import { CONTROL_COMMENTS_LABELS } from './labels'
import { ActivityEntry, commentDedupeKey } from './types'

type ControlCommentsProps = {
  controlId: string
  assessmentId: string
  readOnly?: boolean
  serverEntries?: ActivityEntry[]
  onCommentCountChange?: (count: number) => void
}

export const ControlComments = ({
  controlId,
  assessmentId,
  readOnly,
  serverEntries = [],
  onCommentCountChange,
}: ControlCommentsProps) => {
  const sendMentionNotification = useSendCommentNotification()
  const analytics = useAssessmentAnalytics()
  const trackCommentAdded = useTrackCallback('assessment.comment.added')
  const trackingProps: EventProperties = {
    assessment_id: assessmentId,
    control_id: controlId,
    company_id: analytics?.companyId,
    company: analytics?.companyName,
    source: COMMENT_EVENT_SOURCES.CONTROL_TIMELINE,
  }
  const [isComposerOpen, setIsComposerOpen] = useState(false)
  const closeComposer = () => setIsComposerOpen(false)
  const createThread = useCreateThread()
  const threadsResult = useThreads({ query: { metadata: { controlId } } })
  const { urlParams } = useUrlParams()
  const highlightedCommentId = urlParams[UrlParam.COMMENT_ID]

  const internalThreads = (threadsResult.threads ?? []).filter(isInternalCommentThread)

  const threadEntries: ActivityEntry[] = internalThreads.map((thread) => ({
    key: thread.id,
    timestamp: thread.createdAt,
    node: (
      <CommentThread
        comments={thread.comments}
        readOnly={readOnly}
        highlightedCommentId={highlightedCommentId}
        trackingProps={trackingProps}
      />
    ),
  }))

  const liveCommentKeys = new Set(
    internalThreads.flatMap((thread) =>
      thread.comments.map((comment) => commentDedupeKey(comment.createdAt)),
    ),
  )
  const dedupedServerEntries = serverEntries.filter(
    (entry) => !entry.dedupeKey || !liveCommentKeys.has(entry.dedupeKey),
  )

  const entries = sortByTimestamp(
    [...dedupedServerEntries, ...threadEntries],
    (entry) => entry.timestamp,
  )

  const isEmpty = !threadsResult.isLoading && !threadsResult.error && entries.length === 0

  useEffect(() => {
    if (!highlightedCommentId || threadsResult.isLoading) return
    scrollIntoViewWhenStops(() => document.getElementById(commentAnchorId(highlightedCommentId)))
  }, [highlightedCommentId, threadsResult.isLoading])

  // The parent's badge already counts every server entry, shadows included.
  const shadowedCount = serverEntries.length - dedupedServerEntries.length
  useEffect(() => {
    onCommentCountChange?.(threadEntries.length - shadowedCount)
  }, [threadEntries.length, shadowedCount, onCommentCountChange])

  return (
    <div className='control-comments flex min-h-0 flex-col gap-6'>
      {!readOnly && (
        <div className='flex shrink-0 justify-end'>
          <Button
            variant='secondary'
            size='sm'
            className='gap-2'
            onClick={() => setIsComposerOpen(true)}
          >
            <PlusIcon className='size-4' />
            {CONTROL_COMMENTS_LABELS.addInternalComment}
          </Button>
        </div>
      )}

      <div className='flex min-h-0 flex-col gap-4 overflow-y-auto'>
        {threadsResult.isLoading ? (
          <Loading />
        ) : threadsResult.error ? (
          <CommentsLoadError />
        ) : isEmpty ? (
          <Empty icon='secondary' title='No timeline activity yet' className='mx-auto my-8' />
        ) : (
          <div className='relative flex flex-col gap-6'>
            {entries.map((entry) => (
              <Fragment key={entry.key}>{entry.node}</Fragment>
            ))}
          </div>
        )}
      </div>

      <Modal
        title={CONTROL_COMMENTS_LABELS.addInternalComment}
        size='small'
        open={isComposerOpen}
        showPrimaryButton={false}
        showCancelButton={false}
        onDismiss={closeComposer}
      >
        <ControlCommentComposer
          audience='internal'
          placeholder={CONTROL_COMMENTS_LABELS.composerPlaceholder}
          onSubmit={(comment) => {
            const thread = createThread({
              body: comment.body,
              metadata: { controlId, audience: 'internal' },
            })
            closeComposer()
            trackCommentAdded({ ...trackingProps, ...mentionEventProps(comment.body) })
            sendMentionNotification({
              assessmentId,
              controlId,
              body: comment.body,
              threadId: thread.id,
              commentId: thread.comments[0]?.id,
              trackingProps,
            })
          }}
        />
      </Modal>
    </div>
  )
}
